import clsx from 'clsx';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { use } from 'react';
import { IntercomContextValues, IntercomProvider, useIntercom } from 'react-use-intercom';
import IconButton from '~/components/buttons/NewIconButton';
import IconClose from '~/icons/icon-close.svg?react';
import { AnalyticsEvents } from '~/services/analytics';
import { useIntercomToken } from '~/services/intercom';
import { useLoggedUser } from '~/services/user';
import styles from './IntercomChat.module.scss';
import { useAnalytics } from '../useAnalytics';

const INTERCOM_APP_ID = import.meta.env.VITE_INTERCOM_APP_ID;
const INTERCOM_APP_API_BASE = import.meta.env.VITE_INTERCOM_APP_API_BASE;

interface ChatContextType {
  start: IntercomContextValues['boot'];
  stop: IntercomContextValues['hardShutdown'];
  isStarted: boolean;
  show: () => void;
  hide: () => void;
  isVisible: boolean;
  open: () => void;
  close: () => void;
  isOpen: boolean;
}

const deleteIntercomCookies = () => {
  document.cookie
    .split('; ')
    .filter((cookie) => cookie.startsWith('intercom-') && cookie.endsWith(String(INTERCOM_APP_ID)))
    .forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=' + window.location.hostname;
    });
};

const ChatContext = createContext<ChatContextType | undefined>(undefined);

const showDefaultLauncher = () => {
  // This is a temporary workaround to show the default Intercom launcher only after we initialize it ourselves,
  // as Segment shows it immediately which creates confusion for users
  document.body.classList.add('intercom-ready');
};

const ChatProviderInner: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { boot, hardShutdown, show: showChat, hide: hideChat, update, getVisitorId, isOpen } = useIntercom();
  const { data: userData } = useLoggedUser();
  const { data: intercomToken } = useIntercomToken(userData);
  const [isVisible, setIsVisible] = useState(true);
  const [isStarted, setIsStarted] = useState(false);

  useEffect(() => {
    setIsStarted((started) => {
      if (userData && intercomToken) {
        // add the jwt token and show intercom widget
        if (!started) {
          showDefaultLauncher();
          boot({
            intercomUserJwt: intercomToken,
            hideDefaultLauncher: false,
          });
        } else {
          update({ intercomUserJwt: intercomToken, hideDefaultLauncher: false });
        }
        return true;
      } else {
        // remove Intercom token on logout
        // hardShutdown();
        return false;
      }
    });
  }, [userData, intercomToken, boot, hardShutdown, hideChat, update, getVisitorId]);

  const show = useCallback(() => {
    setIsVisible(true);
    update({ hideDefaultLauncher: false });
  }, [update]);

  const hide = useCallback(() => {
    setIsVisible(false);
    hideChat();
    update({ hideDefaultLauncher: true });
  }, [hideChat, update]);

  const fullShutdown = useCallback(() => {
    hideChat();
    hardShutdown();
    deleteIntercomCookies();
  }, [hardShutdown, hideChat]);

  const value = useMemo(
    () => ({
      start: boot,
      stop: fullShutdown,
      isStarted,
      show,
      hide,
      isVisible,
      open: showChat,
      close: hideChat,
      isOpen,
    }),
    [boot, isStarted, show, hide, isVisible, showChat, hideChat, isOpen, fullShutdown],
  );

  return (
    <ChatContext.Provider value={value}>
      {children}
      <ChatHandler />
    </ChatContext.Provider>
  );
};

const ChatHandler = () => {
  const { hide, isStarted, isVisible, isOpen } = useChat();
  const { track } = useAnalytics();

  if (!isStarted || !isVisible) return;

  const handleClick = () => {
    track(AnalyticsEvents.SUPPORT_CHAT_HIDDEN);
    hide();
  };

  return (
    <IconButton
      className={clsx(styles.handler, {
        [styles.hidden!]: isOpen,
      })}
      size="small"
      onClick={handleClick}
      Icon={IconClose}
      variant="ghost"
    />
  );
};

export const ChatProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  return (
    <IntercomProvider appId={INTERCOM_APP_ID} apiBase={INTERCOM_APP_API_BASE}>
      <ChatProviderInner>{children}</ChatProviderInner>
    </IntercomProvider>
  );
};

export const useChat = () => {
  const context = use(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
